import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useListAgencies } from '~/hooks/apis/agent/useListAgencies';

import { moveUserAgency, previewMoveUserAgency } from '~/services/AgentHub/AgentService';

import { Agency, Agent, PreviewMoveUserAgencyResponse } from '~/types/services/agentHub';

const DEFAULT_SIZE_PER_PAGE = '20';
const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onMoved: (user: Agent) => void;
  userId: string;
  currentAgencyId: string;
}

export default function MoveUserAgencyModal({ isOpen, onClose, onMoved, userId, currentAgencyId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [preview, setPreview] = useState<PreviewMoveUserAgencyResponse | null>(null);

  const setDebouncedSearchDebounced = useMemo(
    () => debounce((next: string) => setDebouncedSearch(next), SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => setDebouncedSearchDebounced.cancel(), [setDebouncedSearchDebounced]);

  const { data, isFetching } = useListAgencies(
    {
      page: '0',
      pageSize: DEFAULT_SIZE_PER_PAGE,
      search: debouncedSearch,
    },
    { enabled: isOpen && debouncedSearch.length > 0 },
  );

  const agencies = useMemo(
    () => (data?.rows ?? []).filter((agency) => agency.id !== currentAgencyId),
    [currentAgencyId, data?.rows],
  );

  const noOptionsText = useMemo(() => {
    if (!search) {
      return 'Start typing to search for agencies';
    }
    if (isFetching) {
      return 'Loading...';
    }
    if (data?.total === 0) {
      return 'No agencies found';
    }
    return 'Start typing to search for agencies';
  }, [data?.total, isFetching, search]);

  const previewMutation = useMutation({
    mutationFn: async (targetAgencyId: string) =>
      previewMoveUserAgency(userId, {
        targetAgencyId,
      }),
  });

  const moveMutation = useMutation({
    mutationFn: async ({
      targetAgencyId,
      deleteOldAgencyIfEmpty,
    }: {
      targetAgencyId: string;
      deleteOldAgencyIfEmpty: boolean;
    }) =>
      moveUserAgency(userId, {
        targetAgencyId,
        deleteOldAgencyIfEmpty,
      }),
  });

  const isSubmitting = previewMutation.isPending || moveMutation.isPending;

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }
    setSelectedAgency(null);
    setPreview(null);
    setSearch('');
    setDebouncedSearchDebounced.cancel();
    setDebouncedSearch('');
    onClose();
  };

  const handleInputChange = (_: SyntheticEvent, value: string) => {
    setSearch(value);
    if (!value.trim()) {
      setDebouncedSearchDebounced.cancel();
      setDebouncedSearch('');
      return;
    }
    setDebouncedSearchDebounced(value);
  };

  const handleMove = async (deleteOldAgencyIfEmpty: boolean) => {
    if (!selectedAgency) {
      return;
    }

    try {
      const updatedUser = await moveMutation.mutateAsync({
        targetAgencyId: selectedAgency.id,
        deleteOldAgencyIfEmpty,
      });
      enqueueSnackbar('Agent agency updated successfully', { variant: 'success' });
      onMoved(updatedUser);
      setSelectedAgency(null);
      setPreview(null);
      setSearch('');
      setDebouncedSearchDebounced.cancel();
      setDebouncedSearch('');
      onClose();
    } catch (err) {
      enqueueSnackbar('Failed to update agent agency', { variant: 'error' });
    }
  };

  const handleSubmit = async () => {
    if (!selectedAgency) {
      return;
    }

    try {
      const movePreview = await previewMutation.mutateAsync(selectedAgency.id);

      if (movePreview.oldAgencyWillBeEmpty) {
        setPreview(movePreview);
        return;
      }

      await handleMove(false);
    } catch (err) {
      enqueueSnackbar('Failed to check agent agency move', { variant: 'error' });
    }
  };

  if (preview) {
    return (
      <Dialog open={isOpen} onClose={closeModal} fullWidth>
        <DialogTitle>Delete empty agency?</DialogTitle>
        <DialogContent>
          <Typography>
            {preview.oldAgencyName} has no agents left in the agency. Do you want to delete {preview.oldAgencyName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => handleMove(false)} disabled={isSubmitting}>
            No
          </Button>
          <Button variant="contained" onClick={() => handleMove(true)} disabled={isSubmitting}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={closeModal} fullWidth>
      <DialogTitle>Move agent to another agency</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Autocomplete
            loading={isFetching}
            options={agencies}
            value={selectedAgency}
            getOptionLabel={(agency) =>
              agency.businessCodeType && agency.businessCodeValue
                ? `${agency.name} (${agency.businessCodeType}: ${agency.businessCodeValue})`
                : agency.name
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(options) => options}
            noOptionsText={noOptionsText}
            onInputChange={handleInputChange}
            onChange={(_, agency) => setSelectedAgency(agency)}
            renderInput={(params) => <TextField {...params} label="Agency" />}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={closeModal} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!selectedAgency || isSubmitting}>
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
}
